import React, { useState } from "react";
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, LabelList } from "recharts";

const pctDe = (otro) => (v, r) => { const t = (v || 0) + (r?.[otro] || 0); return t ? `${Math.round((100 * (v || 0)) / t)}%` : ""; };
const EtiquetaPct = (otro) => function Etiqueta({ x, y, width, height, value, index, data }) {
  if (!height || height < 14 || width < 18) return null;
  return <text x={x + width / 2} y={y + height / 2} fill="#fff" fontSize={width < 34 ? 9 : 11} fontWeight={700} textAnchor="middle" dominantBaseline="central">{pctDe(otro)(value, data[index])}</text>;
};
import { formatMoney, formatMoney2, formatNumber, formatPct } from "../lib/api";

const dec = (v) => (v ?? 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ROWS = [
  { k: "facturacion", label: "Facturación", fmt: formatMoney },
  { k: "facturacion_farmacia", label: "Farmacia", fmt: formatMoney },
  { k: "facturacion_parafarmacia", label: "Parafarmacia", fmt: formatMoney },
  { k: "pct_parafarmacia", label: "% parafarmacia", fmt: formatPct, pp: true },
  { k: "facturacion_libre", label: "Venta libre", fmt: formatMoney },
  { k: "tickets", label: "Operaciones", fmt: formatNumber },
  { k: "ticket_medio", label: "Ticket medio", fmt: formatMoney2 },
  { k: "operaciones_mixtas", label: "Operaciones mixtas", fmt: formatNumber },
  { k: "pct_consejo_en_receta", label: "Ratio de ventas mixtas", fmt: formatPct, pp: true },
  { k: "ratio_productos_libre_receta", label: "Productos libres por receta", fmt: dec },
];

function Variacion({ row, prev, cur }) {
  if (prev == null || cur == null || (!row.pp && !prev)) return <span className="text-slate-400">—</span>;
  const v = row.pp ? (cur - prev) * 100 : (100 * (cur - prev)) / Math.abs(prev);
  const color = v > 0 ? "text-emerald-600" : v < 0 ? "text-red-600" : "text-slate-400";
  return <span className={`font-semibold ${color}`}>{v > 0 ? "+" : ""}{v.toFixed(1)} {row.pp ? "pp" : "%"}</span>;
}

export const VentasComparativa = ({ evolution }) => {
  const n = evolution.length;
  const [baseId, setBaseId] = useState(evolution[n - 2]?.id);
  const [curId, setCurId] = useState(evolution[n - 1]?.id);
  const [all, setAll] = useState(false);
  const base = evolution.find((e) => e.id === baseId) || evolution[n - 2];
  const cur = evolution.find((e) => e.id === curId) || evolution[n - 1];
  const ultimos = evolution.slice(-12);
  const cols = all ? ultimos : [base, cur].filter(Boolean);
  const chart = ultimos.map((e) => ({
    label: e.label,
    Farmacia: e.facturacion_farmacia,
    Parafarmacia: e.facturacion_parafarmacia,
    "Ratio ventas mixtas": e.pct_consejo_en_receta == null ? null : +(e.pct_consejo_en_receta * 100).toFixed(1),
    "% parafarmacia": +((e.pct_parafarmacia || 0) * 100).toFixed(1),
    "Prod. libres por receta": e.ratio_productos_libre_receta ?? null,
  }));
  const pick = (value, onChange, testid) => (
    <select value={value} onChange={(ev) => onChange(ev.target.value)} className="input text-sm py-1.5" style={{ width: 170 }} data-testid={testid}>
      {evolution.map((e) => <option key={e.id} value={e.id}>{e.label}</option>)}
    </select>
  );
  return (
    <>
      <div className="card p-6 mb-6" data-testid="ventas-comparativa">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Comparativa</div>
            <h3 className="font-display text-xl font-bold">Comparar dos meses</h3>
          </div>
          <div className="flex items-center gap-2 flex-wrap text-sm">
            {pick(base?.id, setBaseId, "comparativa-mes-base")}
            <span className="text-slate-400">frente a</span>
            {pick(cur?.id, setCurId, "comparativa-mes-actual")}
            <label className="flex items-center gap-1.5 text-xs text-slate-600 ml-2 cursor-pointer">
              <input type="checkbox" checked={all} onChange={(ev) => setAll(ev.target.checked)} data-testid="comparativa-ver-todos" />
              Ver los últimos 12 meses
            </label>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="dense" data-testid="ventas-comparativa-table">
            <thead>
              <tr>
                <th>Indicador</th>
                {cols.map((e) => (
                  <th key={e.id} className={`num ${!all || e.id === base?.id || e.id === cur?.id ? "" : "opacity-60"}`}>{e.label}</th>
                ))}
                <th className="num">Variación</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r) => (
                <tr key={r.k} data-testid={`ventas-comparativa-row-${r.k}`}>
                  <td className="font-semibold">{r.label}</td>
                  {cols.map((e) => <td key={e.id} className="num">{e[r.k] == null ? "—" : r.fmt(e[r.k])}</td>)}
                  <td className="num"><Variacion row={r} prev={base?.[r.k]} cur={cur?.[r.k]} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="text-[11px] text-slate-500 mt-2" data-testid="comparativa-leyenda">
          Variación de {cur?.label} respecto a {base?.label}. En porcentajes, la diferencia va en puntos (pp).
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <div className="card p-6" data-testid="evolution-farmacia-para">
          <h3 className="font-display text-lg font-bold mb-3">Farmacia vs parafarmacia</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
              <Tooltip formatter={(v) => formatMoney(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Farmacia" stackId="a" fill="#0f172a">
                <LabelList dataKey="Farmacia" content={(p) => EtiquetaPct("Parafarmacia")({ ...p, data: chart })} />
              </Bar>
              <Bar dataKey="Parafarmacia" stackId="a" fill="#8b5cf6" radius={[4, 4, 0, 0]}>
                <LabelList dataKey="Parafarmacia" content={(p) => EtiquetaPct("Farmacia")({ ...p, data: chart })} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="card p-6" data-testid="evolution-ratios">
          <h3 className="font-display text-lg font-bold mb-3">Ventas mixtas y productos libres por receta</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="l" tick={{ fontSize: 11 }} unit="%" />
              <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line yAxisId="l" type="monotone" dataKey="Ratio ventas mixtas" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
              <Line yAxisId="l" type="monotone" dataKey="% parafarmacia" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }} />
              <Line yAxisId="r" type="monotone" dataKey="Prod. libres por receta" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  );
};
